import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { landingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  MessageCircle, 
  Clock, 
  Users, 
  Calendar,
  Shield,
  Droplets,
  Heart,
  Star,
  CheckCircle2,
  MapPin,
  Phone,
  Globe,
  Facebook,
  Instagram,
  Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '26876817515';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;

// Locations data
const LOCATIONS = [
  { value: 'ezulwini', label: 'Ezulwini – Cuddle Puddle' },
  { value: 'manzini', label: 'Manzini Club' },
  { value: 'nhlangano', label: 'Nhlangano – Shingili Plaas' },
  { value: 'mbabane', label: 'Mbabane – Private Pool' },
];

export default function HolidayProgram() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [flyerUrl, setFlyerUrl] = useState(null);
  const [formData, setFormData] = useState({
    parentName: '',
    childAge: '',
    location: '',
    contactNumber: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch logo and flyer from landing config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await landingAPI.getConfig();
        const config = response.data;
        
        // Get logo
        const logo = config.logo_url || config.hero?.logo_url;
        if (logo) {
          setLogoUrl(logo.startsWith('http') ? logo : `${BACKEND_URL}${logo}`);
        }
        
        // Get holiday flyer
        const holidayProgram = config.programs?.find(p => p.id === 'holiday');
        if (holidayProgram?.flyer_url) {
          const flyer = holidayProgram.flyer_url;
          setFlyerUrl(flyer.startsWith('http') ? flyer : `${BACKEND_URL}${flyer}`);
        } else if (holidayProgram?.image_url) {
          const img = holidayProgram.image_url;
          setFlyerUrl(img.startsWith('http') ? img : `${BACKEND_URL}${img}`);
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
      }
    };
    fetchConfig();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (value) => {
    setFormData(prev => ({ ...prev, location: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate brief processing
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 500);
  };

  const getWhatsAppLink = () => {
    const locationLabel = LOCATIONS.find(l => l.value === formData.location)?.label || formData.location;
    const message = encodeURIComponent(
      `Hello Coach Sam, I have submitted the Holiday Program registration.\n\n` +
      `Parent Name: ${formData.parentName}\n` +
      `Child Age: ${formData.childAge}\n` +
      `Preferred Location: ${locationLabel}\n` +
      `Contact Number: ${formData.contactNumber}\n\n` +
      `Please assist me to finalize registration. Thank you.`
    );
    return `${WHATSAPP_LINK}?text=${message}`;
  };

  const isFormValid = formData.parentName && formData.childAge && formData.location && formData.contactNumber;

  return (
    <>
      <Helmet>
        <title>School Holiday Swimming Program | Eswatini Seals Swim Academy</title>
        <meta name="description" content="Full Holiday Swimming Program (Ages 4+). Safe, structured, drowning prevention focused. All inclusive E845 with transport included. Multiple locations. Register via WhatsApp." />
        <meta property="og:title" content="School Holiday Swimming Program | Eswatini Seals Swim Academy" />
        <meta property="og:description" content="Full Holiday Swimming Program (Ages 4+). Safe, structured, drowning prevention focused. All inclusive E845 with transport included. Multiple locations. Register via WhatsApp." />
        {flyerUrl && <meta property="og:image" content={flyerUrl} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-white font-['Outfit',sans-serif]">
        {/* Minimal Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Eswatini Seals" 
                  className="h-10 w-10 object-contain rounded-full"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-[#0f3a4d] flex items-center justify-center">
                  <Droplets className="h-5 w-5 text-[#00c4cc]" />
                </div>
              )}
              <span className="font-semibold text-[#0f3a4d] hidden sm:block">Eswatini Seals</span>
            </div>
            
            {/* WhatsApp Button */}
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          </div>
        </header>

        {/* Hero Section - Authority Version */}
        <section className="pt-20 pb-12 sm:pb-16 relative overflow-hidden min-h-[600px] sm:min-h-[650px]">
          {/* Background Image with Navy Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/cca11250-db11-466a-8b15-8b41e4cf6776/images/09ee70dd7c427b62861747677d59ce8c1d461ab7602426e5de67f70af5b8675b.png"
              alt="Structured swimming lesson with African children"
              className="w-full h-full object-cover"
            />
            {/* Navy overlay at 35% opacity for text readability */}
            <div className="absolute inset-0 bg-[#0f3a4d]/[0.65]" />
          </div>
          
          <div className="max-w-6xl mx-auto px-4 relative z-10">
            <div className="max-w-2xl">
              {/* Left-aligned text block */}
              <div className="text-left">
                {/* Logo */}
                {logoUrl && (
                  <div className="mb-6">
                    <img 
                      src={logoUrl} 
                      alt="Eswatini Seals Swim Academy" 
                      className="h-16 w-16 object-contain rounded-full bg-white/10 p-1"
                    />
                  </div>
                )}
                
                {/* Badge - RETAINED */}
                <div className="inline-flex items-center gap-2 bg-orange-500/90 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                  <Star className="h-4 w-4 fill-current" />
                  LIMITED SPACES AVAILABLE
                </div>
                
                {/* Primary Headline */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  Build Skills. Build Confidence.<br />
                  <span className="text-[#00c4cc]">Prevent Drowning.</span>
                </h1>
                
                {/* Subheadline */}
                <p className="text-xl sm:text-2xl text-white font-medium mb-3">
                  Structured Holiday Swimming Program for Ages 4+
                </p>
                
                {/* Tagline */}
                <p className="text-lg text-white/90 font-semibold mb-6">
                  Safe. Structured. Professionally Supervised.
                </p>
                
                {/* Credibility Strip */}
                <div className="mb-6 space-y-3">
                  {/* Authority Indicators */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-white/95 text-sm sm:text-base">
                    <span className="font-semibold">Since 2013</span>
                    <span className="hidden sm:inline text-white/40">•</span>
                    <span className="font-semibold">22+ Partner Schools Across Eswatini</span>
                  </div>
                  
                  {/* Official Memberships */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 text-white/70 text-xs sm:text-sm">
                    <span>Official Member – Eswatini Swimming Association</span>
                    <span className="hidden sm:inline text-white/40">|</span>
                    <span>Regional Stakeholder – Eswatini Drowning Prevention Alliance (EDPA)</span>
                  </div>
                </div>
                
                {/* Price Banner - RETAINED */}
                <div className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-lg mb-6 shadow-lg">
                  <div className="text-lg sm:text-xl font-bold">ALL INCLUSIVE: E845</div>
                  <div className="text-sm font-medium opacity-90">(Transport Included)</div>
                </div>
                
                {/* Key Details - RETAINED */}
                <div className="flex flex-wrap gap-4 sm:gap-6 mb-8 text-sm sm:text-base">
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white">
                    <Clock className="h-4 w-4 text-[#00c4cc]" />
                    <span>9am – 10am</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white">
                    <Users className="h-4 w-4 text-[#00c4cc]" />
                    <span>From 4 Years</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-white">
                    <Calendar className="h-4 w-4 text-[#00c4cc]" />
                    <span>Full Holiday</span>
                  </div>
                </div>
                
                {/* Primary CTA - RETAINED */}
                <a 
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-lg"
                >
                  <MessageCircle className="h-5 w-5" />
                  BOOK VIA WHATSAPP NOW
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Flyer Image - REMOVED as hero now has full background image */}

        {/* Section 2 - Why This Program */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3a4d] text-center mb-6">
              Why Choose Eswatini Seals Swim Academy?
            </h2>
            
            <p className="text-slate-600 text-center text-lg mb-8 max-w-2xl mx-auto">
              Our Full Holiday Swimming Program builds water confidence, teaches essential survival skills, 
              and introduces structured swimming techniques in a safe and supervised environment.
            </p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Shield, text: 'Water Safety & Survival Skills' },
                { icon: Droplets, text: 'Floating & Breathing Control' },
                { icon: Award, text: 'Basic Stroke Development' },
                { icon: Heart, text: 'Confidence Building' },
                { icon: CheckCircle2, text: 'Structured & Supervised Training' },
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 bg-slate-50 rounded-lg p-4 border border-slate-100"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00c4cc]/10 flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-[#00c4cc]" />
                  </div>
                  <span className="text-[#0f3a4d] font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3 - Locations */}
        <section className="py-12 sm:py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3a4d] text-center mb-8">
              Available Locations
            </h2>
            
            <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
              {LOCATIONS.map((location, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 bg-white rounded-lg p-4 border border-slate-200 shadow-sm"
                >
                  <MapPin className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span className="text-[#0f3a4d] text-sm sm:text-base font-medium">{location.label}</span>
                </div>
              ))}
            </div>
            
            <p className="text-center text-slate-500 mt-6 text-sm">
              Same time applies across all locations.
            </p>
          </div>
        </section>

        {/* Section 4 - Registration Form */}
        <section className="py-12 sm:py-16 bg-white" id="register">
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3a4d] text-center mb-2">
              Secure Your Child's Spot Today
            </h2>
            <p className="text-slate-500 text-center mb-8">
              Complete this short form, then we'll finalize registration via WhatsApp.
            </p>
            
            {!submitted ? (
              <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200">
                <div className="space-y-5">
                  {/* Parent Name */}
                  <div>
                    <Label htmlFor="parentName" className="text-[#0f3a4d] font-medium">
                      Parent Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="parentName"
                      name="parentName"
                      type="text"
                      required
                      value={formData.parentName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="mt-1.5"
                      data-testid="parent-name-input"
                    />
                  </div>
                  
                  {/* Child Age */}
                  <div>
                    <Label htmlFor="childAge" className="text-[#0f3a4d] font-medium">
                      Child Age <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="childAge"
                      name="childAge"
                      type="text"
                      required
                      value={formData.childAge}
                      onChange={handleInputChange}
                      placeholder="e.g., 6 years"
                      className="mt-1.5"
                      data-testid="child-age-input"
                    />
                  </div>
                  
                  {/* Location */}
                  <div>
                    <Label htmlFor="location" className="text-[#0f3a4d] font-medium">
                      Preferred Location <span className="text-red-500">*</span>
                    </Label>
                    <Select onValueChange={handleLocationChange} value={formData.location}>
                      <SelectTrigger className="mt-1.5" data-testid="location-select">
                        <SelectValue placeholder="Select a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCATIONS.map(loc => (
                          <SelectItem key={loc.value} value={loc.value}>
                            {loc.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Contact Number */}
                  <div>
                    <Label htmlFor="contactNumber" className="text-[#0f3a4d] font-medium">
                      Contact Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="contactNumber"
                      name="contactNumber"
                      type="tel"
                      required
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., 7681 7515"
                      className="mt-1.5"
                      data-testid="contact-number-input"
                    />
                  </div>
                  
                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={!isFormValid || loading}
                    className="w-full bg-[#00c4cc] hover:bg-[#00a8af] text-white py-3 text-lg font-semibold"
                    data-testid="submit-form-btn"
                  >
                    {loading ? 'Processing...' : 'Submit Registration'}
                  </Button>
                </div>
                
                {/* Trust line */}
                <p className="text-xs text-slate-400 text-center mt-4">
                  Your information is secure and used only for registration purposes.
                </p>
              </form>
            ) : (
              /* Confirmation Card */
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 sm:p-8 text-center" data-testid="confirmation-card">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                
                <h3 className="text-xl font-bold text-green-800 mb-2">
                  Thanks!
                </h3>
                <p className="text-green-700 mb-6">
                  Tap below to send your details on WhatsApp to complete registration.
                </p>
                
                <a 
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-lg"
                  data-testid="whatsapp-confirm-btn"
                >
                  <MessageCircle className="h-6 w-6" />
                  Complete on WhatsApp
                </a>
                
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ parentName: '', childAge: '', location: '', contactNumber: '' });
                  }}
                  className="block mx-auto mt-4 text-sm text-slate-500 hover:text-slate-700 underline"
                >
                  Submit another registration
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Section 5 - Authority / Trust */}
        <section className="py-12 sm:py-16 bg-[#0f3a4d] text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8">
              Professional & Recognised
            </h2>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 mb-6">
              <div className="flex items-center justify-center gap-2 bg-white/10 px-5 py-3 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-[#00c4cc]" />
                <span>Eswatini Swimming Association</span>
              </div>
              <div className="flex items-center justify-center gap-2 bg-white/10 px-5 py-3 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-[#00c4cc]" />
                <span>Eswatini Drowning Prevention Alliance (EDPA)</span>
              </div>
            </div>
            
            <p className="text-white/80 text-lg">
              Led by Professional Coaches | 13+ Years Experience
            </p>
          </div>
        </section>

        {/* Section 6 - Final CTA */}
        <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-xl mx-auto px-4 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f3a4d] mb-3">
              Ready to Secure Your Child's Spot?
            </h2>
            <p className="text-slate-500 mb-8">
              Limited spaces available across all locations.
            </p>
            
            <a 
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-lg"
              data-testid="final-cta-btn"
            >
              <MessageCircle className="h-5 w-5" />
              REGISTER VIA WHATSAPP NOW
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 bg-[#0f3a4d] text-white border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex flex-col items-center text-center">
              {/* Logo */}
              {logoUrl && (
                <img 
                  src={logoUrl} 
                  alt="Eswatini Seals Swim Academy" 
                  className="h-12 w-12 object-contain rounded-full bg-white/10 p-1 mb-4"
                />
              )}
              
              <h3 className="text-lg font-semibold mb-4">Eswatini Seals Swim Academy</h3>
              
              {/* Contact Info */}
              <div className="space-y-2 text-white/80 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <Globe className="h-4 w-4" />
                  <a href="https://www.sealsswimacademy.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#00c4cc]">
                    www.sealsswimacademy.com
                  </a>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+26876817515" className="hover:text-[#00c4cc]">
                    +268 7681 7515
                  </a>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.facebook.com/sealsofswazilandswimacademy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.instagram.com/eswatiniseals1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.tiktok.com/@eswatini.seals.sw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
                <a 
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#25D366] hover:bg-[#20bd5a] flex items-center justify-center transition-colors"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="h-5 w-5" />
                </a>
              </div>
              
              {/* Copyright */}
              <p className="text-white/50 text-sm mt-6">
                © {new Date().getFullYear()} Eswatini Seals Swim Academy. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
